/**
 * PresetManager - handles preset loading for nodes and graphs
 *
 * Provides standalone functions for loading presets. Runtime uses these
 * to apply preset values when a 'preset' endpoint is set.
 *
 * Preset sources:
 * - Primitives: NodeDef.factoryPresets
 * - GraphInstances: GraphDefinition.presets + GraphDefinition.userPresets
 */

import type { NodeDef } from '../runtime/IRuntime';
import type { GraphDefinition } from '../graph/GraphDefinition';
import { resolveConfigValue, isWrappedConfig } from '../runtime/util';
import { logger } from '../logging';

// ─────────────────────────────────────────────────────────────
// Snapshot types (project-level tweakable param aggregation)
// ─────────────────────────────────────────────────────────────

export interface Snapshot {
  name: string;
  /** Keyed by definitionId, then "nodeId.endpointId" → value */
  values: Record<string, Record<string, any>>;
}

export interface TweaksFile {
  snapshots: Snapshot[];
  /** Duration per snapshot in bars (default 4) */
  snapshotBars: number;
  /** Jukebox mode on/off */
  jukeboxEnabled: boolean;
}

export interface Preset {
  name: string;
  description?: string;
  params: Record<string, any>;
}

const METADATA_KEYS = new Set(['min', 'max', 'step', 'value']);

/**
 * Check if a preset param entry is a metadata descriptor (e.g. { max: 6 } or { value: 1, max: 6 })
 * vs a plain value (string, number, boolean, array).
 */
export function isMetadataDescriptor(value: any): value is Record<string, any> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length > 0 && keys.every(k => METADATA_KEYS.has(k));
}

/**
 * Find a preset by name from a primitive definition.
 */
export function findNodePreset(
  nodeDef: NodeDef | undefined,
  presetName: string
): Preset | undefined {
  if (!nodeDef?.factoryPresets) return undefined;
  return nodeDef.factoryPresets.find(p => p.name === presetName);
}

/**
 * Find a preset by name from a graph definition (factory + user presets).
 */
export function findGraphPreset(
  graphDef: GraphDefinition | undefined,
  presetName: string
): Preset | undefined {
  if (!graphDef) return undefined;

  // Check factory presets first
  const factoryPreset = graphDef.presets?.find(p => p.name === presetName);
  if (factoryPreset) return factoryPreset;

  // Then user presets
  const userPreset = graphDef.userPresets?.find(p => p.name === presetName);
  if (userPreset) return userPreset;

  return undefined;
}

/**
 * Get all preset names for a primitive.
 */
export function getNodePresetNames(nodeDef: NodeDef | undefined): string[] {
  if (!nodeDef?.factoryPresets) return [];
  return nodeDef.factoryPresets.map(p => p.name);
}

/**
 * Get all preset names for a graph definition (factory + user).
 */
export function getGraphPresetNames(graphDef: GraphDefinition | undefined): string[] {
  if (!graphDef) return [];

  const names: string[] = [];
  if (graphDef.presets) {
    names.push(...graphDef.presets.map(p => p.name));
  }
  if (graphDef.userPresets) {
    names.push(...graphDef.userPresets.map(p => p.name));
  }
  return names;
}

/**
 * Apply a preset's params using the provided setter function.
 *
 * @param preset - The preset to apply
 * @param applyValue - Function to apply each param value
 * @returns true if any values were applied
 */
export function applyPreset(
  preset: Preset,
  applyValue: (endpointId: string, value: any) => void
): boolean {
  let applied = false;

  for (const [endpointId, value] of Object.entries(preset.params)) {
    if (value !== undefined) {
      applyValue(endpointId, value);
      applied = true;
    }
  }

  return applied;
}

/**
 * Load and apply a preset for a primitive node.
 *
 * @param nodeDef - The primitive's definition
 * @param presetName - Name of preset to load
 * @param applyValue - Function to apply each param (should persist to config)
 * @returns true if preset was found and applied
 */
export function loadNodePreset(
  nodeDef: NodeDef | undefined,
  presetName: string,
  applyValue: (endpointId: string, value: any) => void
): boolean {
  const preset = findNodePreset(nodeDef, presetName);
  if (!preset) {
    logger.info('PresetManager', `Preset '${presetName}' not found for primitive`);
    return false;
  }

  logger.trace('PresetManager', `Loading primitive preset '${presetName}'`);
  return applyPreset(preset, applyValue);
}

/**
 * Load and apply a preset for a GraphInstance.
 *
 * @param graphDef - The graph's definition
 * @param presetName - Name of preset to load
 * @param applyValue - Function to apply each param (should persist to config)
 * @returns true if preset was found and applied
 */
export function loadGraphPreset(
  graphDef: GraphDefinition | undefined,
  presetName: string,
  applyValue: (endpointId: string, value: any) => void
): boolean {
  const preset = findGraphPreset(graphDef, presetName);
  if (!preset) {
    logger.info('PresetManager', `Preset '${presetName}' not found for graph`);
    return false;
  }

  logger.trace('PresetManager', `Loading graph preset '${presetName}'`);
  return applyPreset(preset, applyValue);
}

// ─────────────────────────────────────────────────────────────
// Project-level snapshot capture
// ─────────────────────────────────────────────────────────────

/**
 * Capture a project-level snapshot of all tweakable params across all definitions.
 *
 * Walks every definition's config, finds entries with `tweakable: true`,
 * and aggregates their current values into a single Snapshot.
 */
export function captureProjectSnapshot(
  definitions: Record<string, GraphDefinition>,
  name: string
): Snapshot {
  const values: Record<string, Record<string, any>> = {};

  for (const [definitionId, definition] of Object.entries(definitions)) {
    const defValues: Record<string, any> = {};

    for (const [nodeId, nodeConfig] of Object.entries(definition.config)) {
      for (const [endpointId, configEntry] of Object.entries(nodeConfig)) {
        if (isWrappedConfig(configEntry) && (configEntry as any).tweakable) {
          const key = `${nodeId}.${endpointId}`;
          defValues[key] = resolveConfigValue(configEntry);
        }
      }
    }

    if (Object.keys(defValues).length > 0) {
      values[definitionId] = defValues;
    }
  }

  return { name, values };
}

/**
 * Backfill a new tweakable param into all existing snapshots.
 * Uses the param's current value so that recalling any snapshot preserves it.
 */
export function backfillSnapshots(
  snapshots: Snapshot[],
  definitionId: string,
  nodeId: string,
  endpointId: string,
  currentValue: any
): void {
  const key = `${nodeId}.${endpointId}`;
  for (const snapshot of snapshots) {
    if (!snapshot.values[definitionId]) {
      snapshot.values[definitionId] = {};
    }
    if (!(key in snapshot.values[definitionId])) {
      snapshot.values[definitionId][key] = currentValue;
    }
  }
}
